import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { LogoutIcon, UserTwoIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { compareAsc } from 'date-fns'
import { Navigate } from 'react-router-dom'

import { constructClientPath } from '../../../../../packages/toolkit/src/utils/functions/construct-path'
import { getEnrollments } from '../../api/enrollments/get-enrollments'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import EnrollmentInformationCard from '../../components/pages/client-space/enrollment-information-card'
import OtoIndividualEnrollmentInformationCard from '../../components/pages/client-space/oto-individual-enrollment-information-card'
import { Paths } from '../../lib/constants/path'
import { useSelectedEnrollment } from '../../lib/contexts/selected-enrollment'
import useAuth from '../../lib/hooks/use-auth'
import useTranslate from '../../lib/hooks/use-translate'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import { enrollmentClientRedirectionFields } from '../../lib/types/fields/enrollments/enrollment'
import { EnrollmentProperties } from '../../lib/types/models/enrollment/enrollment.properties'

const ClientRedirectionPage = () => {
  const { navigate } = useNavigator()
  const { setSelectedEnrollmentId } = useSelectedEnrollment()
  const { user, logout } = useAuth()
  const { t } = useTranslate()

  const { data: enrollments } = useQuery({
    queryKey: ['enrollments', enrollmentClientRedirectionFields],
    queryFn: () => getEnrollments({}, enrollmentClientRedirectionFields),
    enabled: user?.role === RoleEnum.CLIENT
  })
  const groupedEnrollments = EnrollmentProperties.groupEnrollmentsByChild(enrollments?.data ?? [])

  if (!user || user.role !== RoleEnum.CLIENT) {
    return <Navigate replace to={Paths.AUTH_CLIENT_LOGIN_PAGE} />
  }

  if (!enrollments) return <LoadingScreen />

  return (
    <InnerBox
      className="m-12 rounded-2xl flex flex-col h-[calc(100dvh-100px)] overflow-y-auto drop-shadow-2xl shadow bg-white mobile mobile:m-0 mobile:rounded-none mobile:h-dvh mobile:bg-white-hover"
      header={
        <div className="flex justify-between items-center">
          <Typography variant={'h5'} color={tailwindColors.primary.DEFAULT}>
            Bienvenue dans votre espace personnel, {StringFormatter.capitalize(user.firstName)}{' '}
            {user.lastName.toUpperCase()}
          </Typography>
          <Button
            variant={'destructive'}
            icon={LogoutIcon}
            label={t('logout')}
            className={'mt-5 py-4 '}
            onClick={async () => {
              await logout()
              navigate(Paths.AUTH_CLIENT_LOGIN_PAGE)
            }}
          />
        </div>
      }
      headerClassName=" sticky top-0 z-10 bg-white-hover rounded-t-2xl rounded-b-none border-b border-grey-light mobile:rounded-none mobile:border-b-0 mobile:bg-white"
    >
      <div className=" px-5 pt-4 mobile:pt-0">
        <Typography variant="sub-heading" color={tailwindColors.grey.DEFAULT}>
          Sélectionnez une de vos réservations
        </Typography>
        <div className="h-3" />
        <div className="flex flex-col gap-3 mobile:gap-4 mobile-scroll">
          {Object.entries(groupedEnrollments).map(([_, childEnrollments]) => {
            const child = childEnrollments[0] // All enrollments have the same child
            // A child can have several stays, the age shown is the one at the earliest stay departure date
            const [firstStay] = childEnrollments
              .filter((enrollment) => EnrollmentProperties.getAgeAtDeparture(enrollment) !== null)
              .sort((a, b) =>
                compareAsc(
                  EnrollmentProperties.getEnrollmentDepartureAndArrivalDates(a).value?.startDate ??
                    0,
                  EnrollmentProperties.getEnrollmentDepartureAndArrivalDates(b).value?.startDate ??
                    0
                )
              )
            const childAgeAtDeparture = EnrollmentProperties.getAgeAtDeparture(firstStay)
            return (
              <InnerBox
                key={child.id}
                className="rounded-lg p-3 mobile:bg-white mobile:shadow mobile:drop-shadow-md"
              >
                <div className="flex gap-2">
                  <Badge
                    size="flexible"
                    borderColor={tailwindColors.grey.light.DEFAULT}
                    color={tailwindColors.white.hover}
                    className="rounded"
                    icon={<UserTwoIcon color={tailwindColors.secondaryRed.DEFAULT} />}
                  />
                  <div>
                    <Typography variant="tag" color={tailwindColors.secondaryRed.DEFAULT}>
                      {toFullName(child.childFirstName, child.childLastName)}
                    </Typography>
                    <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                      {childAgeAtDeparture != null ? `${childAgeAtDeparture} ans` : '-'}
                    </Typography>
                  </div>
                </div>

                <div className="h-3.5" />
                <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1">
                  {childEnrollments.map((enrollment) => {
                    const onLinkClick = async () => {
                      await setSelectedEnrollmentId(enrollment.id)
                      navigate(
                        constructClientPath(Paths.CLIENT_PERSONAL_SPACE_HOME, enrollment.id)
                      )
                    }

                    return EnrollmentProperties.isOtoIndividual(enrollment) ? (
                      <OtoIndividualEnrollmentInformationCard
                        key={enrollment.id}
                        mode="redirection"
                        enrollment={enrollment}
                        onLinkClick={onLinkClick}
                      />
                    ) : (
                      <EnrollmentInformationCard
                        key={enrollment.id}
                        mode="redirection"
                        enrollment={enrollment}
                        onLinkClick={onLinkClick}
                      />
                    )
                  })}
                </div>
              </InnerBox>
            )
          })}
        </div>
      </div>
    </InnerBox>
  )
}

export default ClientRedirectionPage
